import React, { useCallback, useState } from 'react';
import type { ComponentProps, ComponentType, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { animated, config, useSpring } from 'react-spring';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import {
  SvgAdd,
  SvgAddOutline,
  SvgArrowOutlineRight,
  SvgArrowThickRight,
  SvgCamera,
  SvgChartBar,
  SvgChartPie,
  SvgCheckmarkOutline,
  SvgCog,
  SvgConversation,
  SvgCreditCard,
  SvgPiggyBank,
  SvgReports,
  SvgStoreFront,
  SvgTime,
  SvgTuning,
  SvgWallet,
} from '@actual-app/components/icons/v1';
import { SvgCalendar3 } from '@actual-app/components/icons/v2';
import { styles } from '@actual-app/components/styles';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { useDrag } from '@use-gesture/react';

import { useMtxbudget } from '#components/mtxbudget/useMtxbudget';
import { useIsTestEnv } from '#hooks/useIsTestEnv';
import { useScrollListener } from '#hooks/useScrollListener';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';
import { useDispatch } from '#redux';
import { signOut } from '#users/usersSlice';

const COLUMN_COUNT = 5;
// MTXbudget: the handle is a real button, « Plus d'écrans », tall enough to
// be touched; the drawer holds five rows, sign-out included.
const ROW_COUNT = 5;
const PILL_HEIGHT = 44;
const ROW_HEIGHT = 70;
const TOTAL_HEIGHT = ROW_HEIGHT * ROW_COUNT;
const OPEN_FULL_Y = 1;
const OPEN_DEFAULT_Y = TOTAL_HEIGHT - ROW_HEIGHT;
const HIDDEN_Y = TOTAL_HEIGHT;

export const MOBILE_NAV_HEIGHT = ROW_HEIGHT + PILL_HEIGHT;

export function MobileNavTabs() {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const syncServerStatus = useSyncServerStatus();
  const isTestEnv = useIsTestEnv();
  const isUsingServer = syncServerStatus !== 'no-server' || isTestEnv;
  const dispatch = useDispatch();
  // The amounts the banks do not send, counted on the tab of Ma quinzaine.
  const { toComplete } = useMtxbudget();
  const [navbarState, setNavbarState] = useState<'default' | 'open' | 'hidden'>(
    'default',
  );

  // Labels as the mockups set them, 11 px on two lines at most: a narrow
  // phone keeps « Dettes et projets » whole inside the row.
  const navTabStyle = {
    flex: `1 1 ${100 / COLUMN_COUNT}%`,
    height: ROW_HEIGHT,
    padding: '8px 2px',
    gap: 3,
    fontSize: 11,
    lineHeight: 1.2,
    maxWidth: `${100 / COLUMN_COUNT}%`,
  };

  const [{ y }, api] = useSpring(() => ({ from: { y: OPEN_DEFAULT_Y } }), []);

  const openFull = useCallback(
    ({ canceled }: { canceled?: boolean }) => {
      // when cancel is true, it means that the user passed the upwards threshold
      // so we change the spring config to create a nice wobbly effect
      setNavbarState('open');
      void api.start({
        to: { y: OPEN_FULL_Y },
        immediate: isTestEnv,
        config: canceled ? config.wobbly : config.stiff,
      });
    },
    [api, isTestEnv],
  );

  const openDefault = useCallback(
    (velocity = 0) => {
      setNavbarState('default');
      void api.start({
        to: { y: OPEN_DEFAULT_Y },
        immediate: isTestEnv,
        config: { ...config.stiff, velocity },
      });
    },
    [api, isTestEnv],
  );

  const hide = useCallback(
    (velocity = 0) => {
      setNavbarState('hidden');
      void api.start({
        to: { y: HIDDEN_Y },
        immediate: isTestEnv,
        config: { ...config.stiff, velocity },
      });
    },
    [api, isTestEnv],
  );

  const navTabs = [
    {
      name: t('Quinzaine'),
      path: '/quinzaine',
      style: navTabStyle,
      Icon: SvgTime,
      badge: toComplete.length,
    },
    {
      name: t('Calendrier'),
      path: '/calendrier',
      style: navTabStyle,
      Icon: SvgCalendar3,
    },
    {
      name: t('Prévisions'),
      path: '/previsions',
      style: navTabStyle,
      Icon: SvgAddOutline,
    },
    {
      name: t('Dettes et projets'),
      path: '/dettes-projets',
      style: navTabStyle,
      Icon: SvgChartBar,
    },
    {
      name: t('Budget'),
      path: '/budget',
      style: navTabStyle,
      Icon: SvgWallet,
    },
    {
      name: t('Parcours'),
      path: '/quinzaine/parcours',
      style: navTabStyle,
      Icon: SvgArrowThickRight,
    },
    {
      name: t('À payer'),
      path: '/quinzaine/a-payer',
      style: navTabStyle,
      Icon: SvgCheckmarkOutline,
    },
    {
      name: t('Assistant'),
      path: '/assistant',
      style: navTabStyle,
      Icon: SvgConversation,
    },
    {
      name: t('Analyse'),
      path: '/analyse',
      style: navTabStyle,
      Icon: SvgChartPie,
    },
    {
      name: t('Facture'),
      path: '/facture',
      style: navTabStyle,
      Icon: SvgCamera,
    },
    {
      name: t('État'),
      path: '/etat',
      style: navTabStyle,
      Icon: SvgTuning,
    },
    {
      name: t('Transaction'),
      path: '/transactions/new',
      style: navTabStyle,
      Icon: SvgAdd,
    },
    {
      name: t('Comptes'),
      path: '/accounts',
      style: navTabStyle,
      Icon: SvgPiggyBank,
    },
    {
      name: t('Rapports'),
      path: '/reports',
      style: navTabStyle,
      Icon: SvgReports,
    },
    {
      name: t('Échéances'),
      path: '/schedules',
      style: navTabStyle,
      Icon: SvgCalendar3,
    },
    {
      name: t('Bénéficiaires'),
      path: '/payees',
      style: navTabStyle,
      Icon: SvgStoreFront,
    },
    {
      name: t('Règles'),
      path: '/rules',
      style: navTabStyle,
      Icon: SvgTuning,
    },
    ...(isUsingServer
      ? [
          {
            name: t('Synchro bancaire'),
            path: '/bank-sync',
            style: navTabStyle,
            Icon: SvgCreditCard,
          },
        ]
      : []),
    {
      name: t('Paramètres'),
      path: '/settings',
      style: navTabStyle,
      Icon: SvgCog,
    },
  ]
    .map(tab => (
      <NavTab key={tab.path} onClick={() => openDefault()} {...tab} />
    ))
    .concat(
      <button
        key="sign-out"
        type="button"
        onClick={() => void dispatch(signOut())}
        style={{
          ...styles.noTapHighlight,
          ...navTabStyle,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: 0,
          background: 'none',
          font: 'inherit',
          color: theme.errorText,
          cursor: 'pointer',
        }}
      >
        <SvgArrowOutlineRight
          width={22}
          height={22}
          style={{ minHeight: '22px' }}
        />
        {t('Se déconnecter')}
      </button>,
    );

  const bufferTabsCount =
    (COLUMN_COUNT - (navTabs.length % COLUMN_COUNT)) % COLUMN_COUNT;
  const bufferTabs = Array.from({ length: bufferTabsCount }).map((_, idx) => (
    <div key={idx} style={navTabStyle} />
  ));

  useScrollListener(
    useCallback(
      ({ isScrolling, hasScrolledToEnd }) => {
        if (isScrolling('down') && !hasScrolledToEnd('up')) {
          hide();
        } else if (isScrolling('up') && !hasScrolledToEnd('down')) {
          openDefault();
        }
      },
      [hide, openDefault],
    ),
  );

  const bind = useDrag(
    ({
      last,
      velocity: [, vy],
      direction: [, dy],
      offset: [, oy],
      cancel,
      canceled,
    }) => {
      // if the user drags up passed a threshold, then we cancel
      // the drag so that the sheet resets to its open position
      if (oy < 0) {
        cancel();
      }

      // when the user releases the sheet, we check whether it passed
      // the threshold for it to close, or if we reset it to its open position
      if (last) {
        if (oy > ROW_HEIGHT * 0.5 || (vy > 0.5 && dy > 0)) {
          openDefault(vy);
        } else {
          openFull({ canceled });
        }
      } else {
        // when the user keeps dragging, we just move the sheet according to
        // the cursor position
        void api.start({ to: { y: oy }, immediate: true });
      }
    },
    {
      from: () => [0, y.get()],
      filterTaps: true,
      bounds: { top: -TOTAL_HEIGHT, bottom: TOTAL_HEIGHT - ROW_HEIGHT },
      axis: 'y',
      rubberband: true,
    },
  );

  return (
    <animated.div
      role="navigation"
      {...bind()}
      style={{
        y,
        touchAction: 'pan-x',
        backgroundColor: theme.mobileNavBackground,
        borderTop: `1px solid ${theme.menuBorder}`,
        ...styles.shadow,
        height: TOTAL_HEIGHT + PILL_HEIGHT,
        width: '100%',
        position: 'fixed',
        zIndex: 100,
        bottom: 0,
        ...(!isNarrowWidth && { display: 'none' }),
      }}
      data-navbar-state={navbarState}
    >
      <View>
        <button
          type="button"
          aria-expanded={navbarState === 'open'}
          onClick={() =>
            navbarState === 'open' ? openDefault() : openFull({})
          }
          style={{
            ...styles.noTapHighlight,
            height: PILL_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            border: 0,
            background: 'none',
            font: 'inherit',
            fontSize: 11,
            color: theme.mobileNavItem,
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              width: 30,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.mobileNavItem,
            }}
          />
          {t("Plus d'écrans")}
        </button>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            height: TOTAL_HEIGHT,
            width: '100%',
          }}
        >
          {[navTabs, bufferTabs]}
        </View>
      </View>
    </animated.div>
  );
}

type NavTabIconProps = {
  width: number;
  height: number;
  style?: CSSProperties;
};

type NavTabProps = {
  name: string;
  path: string;
  Icon: ComponentType<NavTabIconProps>;
  style?: CSSProperties;
  onClick: ComponentProps<typeof NavLink>['onClick'];
  /** A count shown on the icon, when there is something to do there. */
  badge?: number;
};

function NavTab({
  Icon: TabIcon,
  name,
  path,
  style,
  onClick,
  badge,
}: NavTabProps) {
  return (
    <NavLink
      to={path}
      end
      style={({ isActive }) => ({
        ...styles.noTapHighlight,
        alignItems: 'center',
        color: isActive ? theme.mobileNavItemSelected : theme.mobileNavItem,
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        textAlign: 'center',
        textWrap: 'balance',
        userSelect: 'none',
        position: 'relative',
        ...style,
      })}
      onClick={onClick}
    >
      <TabIcon width={22} height={22} style={{ minHeight: '22px' }} />
      {badge ? (
        <span
          aria-label={`${badge}`}
          style={{
            position: 'absolute',
            top: 4,
            left: 'calc(50% + 6px)',
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            boxSizing: 'border-box',
            borderRadius: 9,
            backgroundColor: theme.buttonPrimaryBackground,
            color: theme.buttonPrimaryText,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: '18px',
            textAlign: 'center',
          }}
        >
          {badge}
        </span>
      ) : null}
      {name}
    </NavLink>
  );
}
